import React, { useState, useEffect, use } from 'react';
import api from '../api/axiosConfig';
import './QuestionManager.css';
import { toast } from 'react-toastify';

const QuestionManager = () => {
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [type, setType] = useState('MCQ'); // MCQ, TF, SHORT

  const [formData, setFormData] = useState({
    text: '',
    gradeId: localStorage.getItem('qm-lastGrade') || '',
    subjectId: localStorage.getItem('qm-lastSubject') || '',
    topicId: localStorage.getItem('qm-lastTopic') || '',
    points: 10,
    correctAnswerText: '', // For SHORT answer
    options: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ],
    imageUrl: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.gradeId) {
      fetchSubjects(formData.gradeId);
    }
  }, [formData.gradeId]);

  useEffect(() => {
    if (formData.subjectId) {
      fetchTopics(formData.subjectId);
    }
  }, [formData.subjectId]);

  const fetchData = async () => {
    try {
      const [gradeRes, topRes, questRes] = await Promise.all([
        api.get('/admin/grades'),
      ]);
      setGrades(gradeRes.data);
    } catch (err) { console.error("Error loading data"); }
  };

  const fetchSubjects = async (gradeId) => {
    try {
      const res = await api.get(`/admin/subjects?gradeId=${gradeId}`);
      setSubjects(res.data);
    } catch (err) { console.error("Error loading subjects"); }
  };

  const fetchTopics = async (subjectId) => {
    try {
      const res = await api.get(`/admin/topics?subjectId=${subjectId}`);
      setTopics(res.data);
    } catch (err) { console.error("Error loading topics"); }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index].text = value;
    setFormData({ ...formData, options: newOptions });
  };

  const handleCorrectOption = (index) => {
    const newOptions = formData.options.map((opt, i) => ({
      ...opt,
      isCorrect: i === index
    }));
    setFormData({ ...formData, options: newOptions });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {

      // Logic to filter options based on type before sending
      let finalOptions = [];
      if (type === 'MCQ') finalOptions = formData.options;
      if (type === 'TF') finalOptions = formData.options.slice(0, 2);

      await api.post('/admin/questions', { ...formData, type, options: finalOptions });
      toast.success("Question created!");
      fetchData();
    } catch (err) {
      toast.error("Error creating question");
    }
  };

  const onGradeChange = (e) => {
    const gradeId = e.target.value;
    setFormData({ ...formData, gradeId, topicId: '' });
    localStorage.setItem('qm-lastGrade', gradeId);
    localStorage.removeItem('qm-lastSubject');
    localStorage.removeItem('qm-lastTopic');
  };

  const onSubjectChange = (e) => {
    const subjectId = e.target.value;
    setFormData({ ...formData, subjectId, topicId: '' });
    localStorage.setItem('qm-lastSubject', subjectId);
    localStorage.removeItem('qm-lastTopic');
  };

  const onTopicChange = (e) => {
    const topicId = e.target.value;
    setFormData({ ...formData, topicId });
    localStorage.setItem('qm-lastTopic', topicId);
  };

  return (
    <div className="question-container">
      <form onSubmit={handleSubmit} className="question-form">
        <div className="form-title">
          <h3>Create New Question</h3>
        </div>

        <div className="form-header-grid">
          <div className="grade-subject-container">
            {/* Grade Group */}
            <div className="select-group">
              <label>Grade</label>
              <select value={formData.gradeId} onChange={onGradeChange} required>
                <option value="">Select Grade</option>
                {grades.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>

            {/* Subject Group */}
            <div className="select-group">
              <label>Subject</label>
              <select value={formData.subjectId} onChange={onSubjectChange} required>
                <option value="">Select Subject</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {/* Topic Group */}
            <div className="select-group">
              <label>Topic</label>
              <select value={formData.topicId} onChange={onTopicChange} required>
                <option value="">Select Topic</option>
                {topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
        </div>
        {formData.topicId && (
          <div>
            <div class="topic-type-container">
              <select value={type} onChange={e => setType(e.target.value)}>
                <option value="MCQ">Multiple Choice</option>
                <option value="TF">True / False</option>
                <option value="SHORT">Short Answer</option>
              </select>

              <div className="points-input-wrapper">
                <div className="input-with-icon">
                  <input
                    type="number"
                    placeholder="0"
                    value={formData.points}
                    onChange={e => setFormData({ ...formData, points: e.target.value })}
                    min="0"
                  />
                  <span className="points-suffix">PTS</span>
                </div>
              </div>
            </div>
            <div className="image-url-container">
              <div className="image-url-wrapper">
                <div className="input-image-url">
                  <input
                    type="text"
                    placeholder="https://yourdomain.com/image.png"
                    value={formData.imageUrl}
                    onChange={e => setFormData({ ...formData, imageUrl: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="question-text-section">
              <textarea
                placeholder="Enter your question here..."
                value={formData.text}
                onChange={e => setFormData({ ...formData, text: e.target.value })}
                required
              />
            </div>

            <div className="answer-setup-section">
              <h4>{type === 'SHORT' ? 'Define Correct Answer' : 'Set Options & Correct Answer'}</h4>

              {type === 'SHORT' ? (
                <div className="short-answer-wrapper">
                  <input
                    className="short-answer-input"
                    placeholder="e.g., Photosynthesis"
                    value={formData.correctAnswerText}
                    onChange={e => setFormData({ ...formData, correctAnswerText: e.target.value })}
                  />
                </div>
              ) : (
                <div className="options-grid">
                  {(type === 'MCQ' ? [0, 1, 2, 3] : [0, 1]).map(i => (
                    <div key={i} className={`option-card ${formData.options[i].isCorrect ? 'is-correct' : ''}`}>
                      <input
                        type="radio"
                        name="correct-ans"
                        checked={formData.options[i].isCorrect}
                        onChange={() => handleCorrectOption(i)}
                      />
                      <input
                        type="text"
                        placeholder={type === 'TF' ? (i === 0 ? "True" : "False") : `Option ${i + 1}`}
                        value={formData.options[i].text}
                        onChange={e => handleOptionChange(i, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" className="submit-btn">Create Question</button>
          </div>)}
      </form>

      {/* Question List Table can go here */}
    </div>
  );
};

export default QuestionManager;